import { PureComponent, Fragment } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';

import { appDispatch, RootState } from 'store/store';

import messages from './Messages';
import Button from 'components/Button';
import Modal from 'components/Modal';
import CheckPointEditor from 'components/CheckPointEditor';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import styled from 'styled-components';
import Icon from '../Icon/Icon';
import {
  ICheckPoint,
  IProviderSurveyReport,
  IReportView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import {
  deleteCheckPoint,
  deleteCheckPointFn,
  saveCheckPoint,
  saveCheckPointFn,
} from 'store/survey/surveySlice';
import { IOpenItem } from 'store/entity/IOpenItem';
import { ITags } from 'store/providers/providerTypes';
import i18n from '../../i18n';

interface IStateToProps {
  checkPoints: Array<ICheckPoint>;
  surveyCheckPoints: Array<ICheckPoint>;
  tags: ITags['data'];
}

const HeadWrapper = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

interface IDispatchToProps {
  saveCheckPoint: (
    checkPoint: ICheckPoint,
    isUpdate: boolean,
    surveyToken?: string,
    ownerToken?: string
  ) => ReturnType<typeof saveCheckPointFn>;
  deleteCheckPoint: (
    checkPointId: number,
    surveyToken: string,
    ownerToken: string
  ) => ReturnType<typeof deleteCheckPointFn>;
}

interface IOwnProps {
  active: boolean;
  canUpdate: boolean;
  onClose: () => void;
  report?: IReportView;
  survey?: ISurveyView;
  surveyReport?: IOpenItem;
  providerId?: number;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  newCheckPoint: boolean;
  newSurveyCheckPoint: boolean;
}

class CheckPointModal extends PureComponent<IComponentProps, IComponentStates> {
  state = {
    newCheckPoint: false,
    newSurveyCheckPoint: false,
  };

  handleAddCheckPoint = () => {
    this.setState((prevState) => ({ newCheckPoint: !prevState.newCheckPoint }));
  };
  handleAddSurveyCheckPoint = () => {
    this.setState((prevState) => ({
      newSurveyCheckPoint: !prevState.newSurveyCheckPoint,
    }));
  };

  handleSaveCheckPoint = (checkPoint: ICheckPoint, isUpdate: boolean) => {
    let surveyToken;
    let ownerToken;
    if (this.props.survey) {
      surveyToken = this.props.survey.surveyToken;
    } else if (this.props.surveyReport) {
      ownerToken = (this.props.surveyReport.item.data as IProviderSurveyReport)
        .ownerToken
        ? (this.props.surveyReport.item.data as IProviderSurveyReport)
            .ownerToken
        : (this.props.surveyReport.item.data as IProviderSurveyReport)
            .reportToken;
    }

    return this.props
      .saveCheckPoint(checkPoint, isUpdate, surveyToken, ownerToken)
      .then(() => {
        notify(messages.checkPointSavedSuccessfully(isUpdate));
        if (!isUpdate) {
          this.setState({ newCheckPoint: false, newSurveyCheckPoint: false });
        }
        return Promise.resolve();
      })
      .catch((err: any) => {
        display(err);
        return Promise.reject();
      });
  };

  handleDeleteCheckPoint = (checkPointId: number) => {
    let surveyToken: string = '';
    let ownerToken: string = '';
    if (this.props.survey) {
      surveyToken = this.props.survey.surveyToken!;
    } else if (this.props.surveyReport) {
      ownerToken = (this.props.surveyReport.item.data as IProviderSurveyReport)
        .ownerToken
        ? (this.props.surveyReport.item.data as IProviderSurveyReport)
            .ownerToken!
        : (this.props.surveyReport.item.data as IProviderSurveyReport)
            .reportToken!;
    }

    return this.props
      .deleteCheckPoint(checkPointId, surveyToken, ownerToken)
      .then(() => {
        notify(i18n.t('checkPointRemoved', { ns: 'survey' }));
      })
      .catch((err: any) => {
        display(err);
      });
  };

  render() {
    const { active, onClose, checkPoints, surveyCheckPoints, survey } =
      this.props;
    return (
      <Modal
        isActive={active}
        onClose={onClose}
        title={i18n.t('checkPoints', { ns: 'survey' }) as string}
      >
        {this.props.survey && 2 === 1 - 2 /*disabled for now*/ && (
          <>
            <HeadWrapper>
              <h4>{i18n.t('surveyGoals', { ns: 'survey' }) as string}</h4>
              {this.props.canUpdate && (
                <Button color="green" onClick={this.handleAddSurveyCheckPoint}>
                  <Icon type="plus" />{' '}
                  {i18n.t('addSurveyCheckPoint', { ns: 'survey' }) as string}
                </Button>
              )}
            </HeadWrapper>
            <div>
              {this.state.newSurveyCheckPoint && (
                <CheckPointEditor
                  canUpdate={this.props.canUpdate}
                  onCancel={this.handleAddCheckPoint}
                  onRemove={this.handleDeleteCheckPoint}
                  onSave={this.handleSaveCheckPoint}
                  surveyId={
                    this.props.survey ? this.props.survey.id : undefined
                  }
                  survey={survey}
                  tags={this.props.tags}
                />
              )}
              {surveyCheckPoints.length > 0 ? (
                <Fragment>
                  {Object.values(surveyCheckPoints)
                    .sort((a, b) => {
                      if (b.date && a.date) return b.date - a.date;

                      return 0;
                    })
                    .map((checkPoint: ICheckPoint) => (
                      <CheckPointEditor
                        key={checkPoint.id}
                        canUpdate={this.props.canUpdate}
                        checkPoint={checkPoint}
                        onRemove={this.handleDeleteCheckPoint}
                        onSave={this.handleSaveCheckPoint}
                        surveyId={
                          this.props.survey ? this.props.survey.id : undefined
                        }
                        survey={survey}
                        tags={this.props.tags}
                      />
                    ))}
                </Fragment>
              ) : (
                <div style={{ margin: '40px', textAlign: 'center' }}>
                  {
                    i18n.t('thereIsNoCheckPointsYet', {
                      ns: 'survey',
                    }) as string
                  }
                </div>
              )}
            </div>
          </>
        )}
        {this.props.report && (
          <>
            <HeadWrapper>
              <h4>{i18n.t('specificGoals', { ns: 'survey' }) as string}</h4>
              {this.props.canUpdate && (
                <Button color="green" onClick={this.handleAddCheckPoint}>
                  <Icon type="plus" />{' '}
                  {i18n.t('addCheckPoint', { ns: 'survey' }) as string}
                </Button>
              )}
            </HeadWrapper>
            <div>
              {this.state.newCheckPoint && (
                <CheckPointEditor
                  canUpdate={this.props.canUpdate}
                  onCancel={this.handleAddCheckPoint}
                  onRemove={this.handleDeleteCheckPoint}
                  onSave={this.handleSaveCheckPoint}
                  reportId={
                    this.props.report ? this.props.report.id : undefined
                  }
                  surveyId={
                    this.props.survey ? this.props.survey.id : undefined
                  }
                  survey={survey}
                  tags={this.props.tags}
                />
              )}
              {checkPoints.length > 0 ? (
                <Fragment>
                  {Object.values(checkPoints)
                    .sort((a, b) => {
                      if (b.date && a.date) return b.date - a.date;

                      return 0;
                    })
                    .map((checkPoint: ICheckPoint) => (
                      <CheckPointEditor
                        key={checkPoint.id}
                        canUpdate={this.props.canUpdate}
                        checkPoint={checkPoint}
                        onRemove={this.handleDeleteCheckPoint}
                        onSave={this.handleSaveCheckPoint}
                        surveyId={
                          this.props.survey ? this.props.survey.id : undefined
                        }
                        reportId={
                          this.props.report ? this.props.report.id : undefined
                        }
                        survey={survey}
                        tags={this.props.tags}
                      />
                    ))}
                </Fragment>
              ) : (
                <div style={{ margin: '40px', textAlign: 'center' }}>
                  {
                    i18n.t('thereIsNoCheckPointsYet', {
                      ns: 'survey',
                    }) as string
                  }
                </div>
              )}
            </div>
          </>
        )}
      </Modal>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const relatedCheckpoints = Object.values(
    state.survey.checkPoints?.data || {}
  ).filter((checkPoint) => {
    if (props.survey) {
      return checkPoint.surveyId === props.survey.id;
    }
    return true;
  });
  const checkPoints = relatedCheckpoints.filter((checkPoint) => {
    if (props.report) {
      return checkPoint.reportId === props.report.id;
    }
    return false;
  });
  const surveyCheckPoints: Array<ICheckPoint> = relatedCheckpoints.filter(
    (checkPoint: ICheckPoint) => {
      return !checkPoint.reportId;
    }
  );
  const tags = state.providers.tags[props.providerId!]!.data;
  return { checkPoints, surveyCheckPoints, tags };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  saveCheckPoint: (
    checkPoint: ICheckPoint,
    isUpdate: boolean,
    surveyToken?: string,
    ownerToken?: string
  ) =>
    appDispatch(
      saveCheckPoint({ checkPoint, isUpdate, surveyToken, ownerToken })
    ),
  deleteCheckPoint: (
    checkPointId: number,
    surveyToken: string,
    ownerToken: string
  ) => appDispatch(deleteCheckPoint({ checkPointId, surveyToken, ownerToken })),
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckPointModal);
